import { Box, Grid, Button, Typography, IconButton } from "@material-ui/core";
import { Form, Modal } from "@components";
import React, { useCallback, useContext, useState, useEffect } from "react";
import { useHistory } from "react-router";
import { UPDATE_FILTERS_DATA } from "../state";
import { TableContext } from "../table";
import { Search } from "./search-filters";
import { Actions } from "../../../screens/request/components/actions";
import CloseIcon from "@material-ui/icons/Close";
import { Api } from "@api";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from "@material-ui/core/styles";
import { FormData } from "../../../screens/dashboard/admin/maintenance/category/detail/form";
import { alertService } from "@services";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

interface Props {
  onClickAdd: () => void;
  //onShowAdd: () => void;
}

type FormValues = {
  name: string;
  isActive: boolean;
  isManual: boolean;
  businessUnit: {
    id: number;
    name: string;
  };
};

type Key = keyof FormValues;

export const CategoryToolbar: React.FC<Props> = ({ onClickAdd }) => {
  const history = useHistory();
  const { dispatch } = useContext(TableContext);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = React.useState<
    boolean
  >(false);
  const [pending, setPending] = React.useState(false);
  const [isEmptyBusinessUnit, setIsEmptyBusinessUnit] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const onSearchUpdate = useCallback(
    (term: string) => {
      dispatch({
        type: UPDATE_FILTERS_DATA,
        filtersData: { searchTerm: term },
      });
    },
    [dispatch]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = Form.useManager<FormValues>({
    name: "",
    isActive: false,
    isManual: false,
    businessUnit: {
      id: 0,
      name: "",
    },
  });

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });

    if (name === "businessUnit") {
      checkIfBusinessUnitIsSelected();
    }
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "name":
      case "businessUnit":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function handleAddButtonClick(): void {
    if (!form.values.businessUnit.id || !form.values.name) {
      alertService.danger({
        msg: "Please complete required field(s).",
      });
      return;
    }

    setPending(true);
    setIsSubmitting(true);
    Api.category
      .postCategory({
        ...form.values,
      })
      .then(() => {
        alertService.success({
          msg: "System category has been successfully created.",
        });
        setPending(false);
        setIsOpenConfirmationModal(false);
        form.reset();
        handleClose();
        onClickAdd();
        setIsFirstRender(true);
      })
      .catch((error) => {
        if (error.response) {
          alertService.danger({ statusCode: error.response.status });
        }
        setPending(false);
        // [TODO] Handle error
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  useEffect(() => {
    if (isFirstRender) {
      setIsEmptyBusinessUnit(false);
      setIsFirstRender(false);
    }
  }, [form.values.businessUnit.id]);

  const checkIfBusinessUnitIsSelected = () => {
    form.values.businessUnit.id
      ? setIsEmptyBusinessUnit(false)
      : setIsEmptyBusinessUnit(true);
  };

  function handleSubmitButtonClick(): void {
    checkIfBusinessUnitIsSelected();

    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          if (!isEmptyBusinessUnit) {
            handleAddButtonClick();
          }
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    form.reset();
    setIsEmptyBusinessUnit(false);
    setIsFirstRender(true);
    setOpen(false);
  };
  const styles = (theme: Theme) =>
    createStyles({
      root: {
        margin: 0,
        padding: theme.spacing(2),
      },
      closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
      },
    });

  const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <Typography variant="h6">{children}</Typography>
        {onClose ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    );
  });
  const submitText = isSubmitting ? "Submitting..." : "Submit";
  return (
    <Box mt={2} mb={2}>
      <Grid container direction="row" spacing={1} justify="space-between">
        <Grid item xs={"auto"}>
          <Actions>
            <Actions.Action onClick={handleClickOpen}>
              + Add New System Category
            </Actions.Action>
          </Actions>
        </Grid>
        <Grid item xs={3}>
          <Search
            onSearchUpdate={onSearchUpdate}
            placeholder={"Search by Name"}
          ></Search>
        </Grid>
      </Grid>
      <Modal
        isOpen={isOpenConfirmationModal}
        positionButton="center"
        actions={[
          {
            label: "Cancel",
            variant: "outlined",
            handler: () => {
              setIsOpenConfirmationModal(false);
            },
          },
          {
            label: pending ? "Submitting..." : "Submit",
            handler: () => {
              handleAddButtonClick();
            },
            disabled: pending,
          },
        ]}
      >
        <Typography align="center" paragraph>
          Are you sure you want to add this System Category?
        </Typography>
      </Modal>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        fullWidth={true}
        maxWidth={"sm"}
      >
        <DialogTitle id="form-dialog-title" onClose={handleClose}>
          Add New System Category
        </DialogTitle>
        <DialogContent>
          <FormData
            isEmptyBusinessUnit={isEmptyBusinessUnit}
            data={undefined}
            onHandleChange={onChange}
            form={form}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            disabled={isSubmitting}
            onClick={handleSubmitButtonClick}
            color="primary"
          >
            {submitText}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
